import { FunctionComponent } from 'react';
import { YourDetailsTemplate, UserData } from '@templates';
import { ErrorMessages } from '@types';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../routes/Routes';
import { useUserData } from '@hooks';
import {
  msalInstance,
  handleLoginPageEvent,
  yourDetailsSchema,
  parseZodErrors,
  LoginRequestConfig,
  handleLoginRequest,
} from '@utils';
import { ChangeEvent, useEffect, useState } from 'react';
import { useWasteStore } from '@waste/store/store';
import { ZodError } from 'zod';
import { useHandleSubmit } from '@waste/hooks/useHandleSubmit';
import { WastePayload } from '../payload/Payload';

export const YourDetails: FunctionComponent = () => {
  const navigate = useNavigate();
  const payload = WastePayload();
  const { mutate, isPending } = useHandleSubmit();
  const {
    updateFirstName,
    updateLastName,
    updateEmail,
    firstName,
    lastName,
    email,
  } = useWasteStore();

  const formData = {
    firstName,
    lastName,
    email,
  };
  const [crmId, setCrmId] = useState<string>('');
  const [idProvider, setIdProvider] = useState<string>('');
  const { isLoggedInUser, contactData, isLoading, isFetching } =
    useUserData(crmId);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const isLoadingUserData = isLoading || isFetching;

  useEffect(() => {
    if (!crmId) {
      handleLoginPageEvent(msalInstance, setCrmId, setIdProvider);
    }
    if (contactData) {
      const data = {
        email: contactData.emailaddress1 || '',
        firstName: contactData.firstname || '',
        lastName: contactData.lastname || '',
      };
      updateFirstName(data.firstName);
      updateLastName(data.lastName);
      updateEmail(data.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactData, crmId, idProvider]);

  const handleSubmit = () => {
    const { firstName, lastName, email } = formData;
    try {
      yourDetailsSchema.parse({ firstName, lastName, email });

      if (firstName && lastName && email && !isPending) {
        mutate(payload);
      }
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrorMessages = parseZodErrors(error);
        setErrorMessages(newErrorMessages);
        throw new Error();
      }
    }
  };

  const loginConfig: LoginRequestConfig = {
    msal: msalInstance,
    navigate: () => navigate(RoutesList.YOUR_DETAILS, { replace: true }),
  };

  const handleLogin = () => handleLoginRequest(loginConfig);

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.EMAIL_CONFIRMATION_REPORT);
    },
  };

  const buttonNext = {
    label: 'Submit',
    loading: isPending,
    onClick: () => {
      handleSubmit();
    },
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    if (name === 'firstName') {
      updateFirstName(value);
    } else if (name === 'lastName') {
      updateLastName(value);
    } else if (name === 'email') {
      updateEmail(value);
    }
    setErrorMessages([]);
  };

  return (
    <YourDetailsTemplate
      isLoadingUserData={isLoadingUserData}
      isLoggedInUser={isLoggedInUser}
      userData={formData}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      errorMessages={errorMessages}
      handleLogin={handleLogin}
      onChange={handleChange}
      idProvider={idProvider}
    />
  );
};
